// Countries GaintPlay is closed in (Admin → Countries, "Blocked"). When the visitor is there, or a
// signed-in member's account is from there (the API answers COUNTRY_BLOCKED), this notice takes the
// place of the whole site, with the one thing still open: writing to support by email. Until the
// answer arrives, or if it can't be read, the site opens as usual; the API refuses on its own.
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Globe2 } from 'lucide-react';
import { ApiError } from '../lib/api';
import { meQuery } from '../lib/session';
import { countryQuery } from '../lib/queries';
import { getLocale, T, t } from '../i18n';
import { GateScreen, SUPPORT_EMAIL, useLeave } from './GateScreen';

export function CountryGate({ children }: { children: ReactNode }) {
  const { data } = useQuery(countryQuery);
  // The session as the rest of the site reads it. Only when the country is closed does this ask
  // for it itself (the site isn't there to): a member gets "Log out" on the notice.
  const me = useQuery({ ...meQuery, enabled: data?.access === 'blocked' });
  const refused = me.error instanceof ApiError && me.error.code === 'COUNTRY_BLOCKED';
  const member = refused || !!me.data;
  const blocked = data?.access === 'blocked' || refused;
  if (!blocked) return <>{children}</>;
  return (
    <BlockedScreen country={data?.access === 'blocked' ? data.country : null} member={member} />
  );
}

function countryName(code: string | null): string | null {
  if (!code) return null;
  try {
    return new Intl.DisplayNames([getLocale()], { type: 'region' }).of(code) ?? null;
  } catch {
    return null;
  }
}

function BlockedScreen({ country, member }: { country: string | null; member: boolean }) {
  const leave = useLeave();
  const name = countryName(country);
  return (
    <GateScreen
      id="country-closed-title"
      // i18n: the browser tab's title when GaintPlay is closed in the visitor's country
      docTitle={t('Not available in your country')}
      eyebrow={
        name && (
          <>
            <Globe2 aria-hidden="true" size={14} strokeWidth={2} />
            {name}
          </>
        )
      }
      title={t('GaintPlay isn’t available in your country')}
      actions={
        <>
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="btn-primary pressable min-h-[52px] w-full text-[16px]"
          >
            {
              // i18n: button that opens the email app to write to support
              t('Email support')
            }
          </a>
          {member && (
            <button
              type="button"
              onClick={() => void leave()}
              className="btn-ghost pressable mt-3 min-h-[52px] w-full text-[16px]"
            >
              {t('Log out')}
            </button>
          )}
        </>
      }
    >
      <p className="mt-3 text-pretty text-[16px] leading-relaxed text-graphite">
        {t(
          'We can’t offer GaintPlay where you are right now, so signing up, earning and cashing out are closed here.',
        )}
      </p>
      <div className="card mt-6 p-4">
        <p className="text-[15px] leading-snug text-graphite">
          <T
            msg="<b>Questions about your account?</b> Write to {email} and we’ll answer there."
            values={{ email: SUPPORT_EMAIL }}
            tags={{ b: (c) => <span className="font-semibold text-ink">{c}</span> }}
          />
        </p>
      </div>
    </GateScreen>
  );
}
